import { Component, OnInit, isDevMode } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { CentreOption } from '../../../../interfaces/Centres';
import { CentreService } from '../../../../services/centre.service';
import { environment } from '../../../../../environments/environment';
import { Timestamp } from 'firebase/firestore';
import * as XLSX from 'xlsx';

interface CentreStayRow {
  centreCode: string;
  centreName: string;
  newCount: number;
  newAvgDays: number;
  followUpCount: number;
  followUpAvgDays: number;
  carryForwardCount: number;
  carryForwardAvgDays: number;
  totalCount: number;
  totalAvgDays: number;
}

const EMPTY_TOTAL: CentreStayRow = {
  centreCode: 'TOTAL', centreName: 'Grand Total',
  newCount: 0, newAvgDays: 0,
  followUpCount: 0, followUpAvgDays: 0,
  carryForwardCount: 0, carryForwardAvgDays: 0,
  totalCount: 0, totalAvgDays: 0
};

@Component({
  selector: 'app-average-stay-report',
  templateUrl: './average-stay-report.component.html',
  styleUrls: ['./average-stay-report.component.scss']
})
export class AverageStayReportComponent implements OnInit {
  readonly env = isDevMode() ? environment.testMode : environment.productionMode;

  start: Date | undefined;
  end: Date | undefined;
  selectedCentreCode: string = '';
  includeCarryForward = true;

  centres: Observable<CentreOption[]>;
  isLoading = false;

  tableRows: CentreStayRow[] = [];
  dataSource = new MatTableDataSource<CentreStayRow>();
  grandTotalRow: CentreStayRow = { ...EMPTY_TOTAL };

  displayedColumns = [
    'centreName',
    'newCount', 'newAvgDays',
    'followUpCount', 'followUpAvgDays',
    'carryForwardCount', 'carryForwardAvgDays',
    'totalCount', 'totalAvgDays'
  ];

  constructor(
    private readonly mFirestore: AngularFirestore,
    private readonly mCentreService: CentreService,
    private readonly mSnackBar: MatSnackBar
  ) {
    this.centres = this.mCentreService.getAllCentresOptions();
  }

  ngOnInit(): void {}

  search() {
    if (!this.start || !this.end) {
      this.mSnackBar.open('Please select a date range', '', { duration: 2500, panelClass: 'error' });
      return;
    }

    this.isLoading = true;
    this.tableRows = [];

    const rangeStart = new Date(new Date(this.start).setHours(0, 0, 0, 0));
    const rangeEnd = new Date(new Date(this.end).setHours(23, 59, 59, 999));
    const startTs = Timestamp.fromDate(rangeStart);
    const endTs = Timestamp.fromDate(rangeEnd);

    // Three queries mirroring revised-child-report's carry-forward cases, but
    // shaped to need only Firestore's automatic single-field indexes:
    //   1. admitted within range
    //   2. discharged on/after range start (carry-forwards with a discharge;
    //      also re-fetches some in-range docs — de-duplicated by doc id below)
    //   3. still admitted (no discharge date) — admission date filtered client-side
    const col = this.env.admissionCycles;
    const queries: Promise<any>[] = [
      this.mFirestore.collection(col, ref =>
        ref.where('dateOfAdmission', '>=', startTs)
           .where('dateOfAdmission', '<=', endTs)
      ).get().toPromise()
    ];

    if (this.includeCarryForward) {
      queries.push(
        this.mFirestore.collection(col, ref =>
          ref.where('dateOfDischarge', '>=', startTs)
        ).get().toPromise(),
        this.mFirestore.collection(col, ref =>
          ref.where('dateOfDischarge', '==', null)
        ).get().toPromise()
      );
    }

    Promise.all(queries)
      .then(snaps => {
        const cyclesById = new Map<string, any>();
        for (const snap of snaps) {
          snap?.docs.forEach((d: any) => cyclesById.set(d.id, { id: d.id, ...d.data() }));
        }

        let cycles = Array.from(cyclesById.values());
        if (this.selectedCentreCode) {
          cycles = cycles.filter(c => c.centreCode === this.selectedCentreCode);
        }

        this.tableRows = this.buildRows(cycles, rangeStart, rangeEnd);
        this.dataSource.data = this.tableRows;
        this.isLoading = false;

        if (this.tableRows.length === 0) {
          this.mSnackBar.open('No data found for the selected range', '', { duration: 2500 });
        }
      })
      .catch(err => {
        console.error('Error fetching admission cycles:', err);
        this.isLoading = false;
        this.mSnackBar.open('Error fetching data', '', { duration: 2500, panelClass: 'error' });
      });
  }

  private buildRows(cycles: any[], rangeStart: Date, rangeEnd: Date): CentreStayRow[] {
    const centreMap = new Map<string, {
      centreName: string;
      newDays: number[]; followUpDays: number[]; carryForwardDays: number[];
    }>();

    const now = new Date();

    for (const cycle of cycles) {
      const admission = this.toDate(cycle.dateOfAdmission);
      if (!admission) {
        console.warn('Skipping cycle with missing/invalid dateOfAdmission:', cycle.id);
        continue;
      }
      const discharge = this.toDate(cycle.dateOfDischarge);

      const admittedInRange = admission >= rangeStart && admission <= rangeEnd;
      // Carry-forward: admitted before the range and still present at range start
      const isCarryForward = !admittedInRange
        && admission < rangeStart
        && (!discharge || discharge >= rangeStart);

      if (!admittedInRange && !(this.includeCarryForward && isCarryForward)) continue;

      // Days of stay clipped to the range (occupancy within the period).
      // Open cycles are capped at the range end, never counted past today.
      const effStart = admission > rangeStart ? admission : rangeStart;
      const rawEnd = discharge ?? (now < rangeEnd ? now : rangeEnd);
      const effEnd = rawEnd < rangeEnd ? rawEnd : rangeEnd;
      const days = Math.max(0, Math.round((effEnd.getTime() - effStart.getTime()) / 86400000));

      const cc: string = cycle.centreCode || 'Unknown';
      const cn: string = cycle.centreName || cc;

      if (!centreMap.has(cc)) {
        centreMap.set(cc, { centreName: cn, newDays: [], followUpDays: [], carryForwardDays: [] });
      }
      const entry = centreMap.get(cc)!;

      if (isCarryForward) {
        entry.carryForwardDays.push(days);
      } else if (cycle.isFollowUp) {
        entry.followUpDays.push(days);
      } else {
        entry.newDays.push(days);
      }
    }

    const rows: CentreStayRow[] = [];
    const grandNew: number[] = [];
    const grandFu: number[] = [];
    const grandCf: number[] = [];

    centreMap.forEach((data, centreCode) => {
      const allDays = [...data.newDays, ...data.followUpDays, ...data.carryForwardDays];
      grandNew.push(...data.newDays);
      grandFu.push(...data.followUpDays);
      grandCf.push(...data.carryForwardDays);

      rows.push({
        centreCode,
        centreName: data.centreName,
        newCount: data.newDays.length,
        newAvgDays: data.newDays.length ? this.avg(data.newDays) : 0,
        followUpCount: data.followUpDays.length,
        followUpAvgDays: data.followUpDays.length ? this.avg(data.followUpDays) : 0,
        carryForwardCount: data.carryForwardDays.length,
        carryForwardAvgDays: data.carryForwardDays.length ? this.avg(data.carryForwardDays) : 0,
        totalCount: allDays.length,
        totalAvgDays: allDays.length ? this.avg(allDays) : 0
      });
    });

    const grandAll = [...grandNew, ...grandFu, ...grandCf];
    this.grandTotalRow = {
      ...EMPTY_TOTAL,
      newCount: grandNew.length,
      newAvgDays: grandNew.length ? this.avg(grandNew) : 0,
      followUpCount: grandFu.length,
      followUpAvgDays: grandFu.length ? this.avg(grandFu) : 0,
      carryForwardCount: grandCf.length,
      carryForwardAvgDays: grandCf.length ? this.avg(grandCf) : 0,
      totalCount: grandAll.length,
      totalAvgDays: grandAll.length ? this.avg(grandAll) : 0
    };

    return rows.sort((a, b) => a.centreName.localeCompare(b.centreName));
  }

  private avg(arr: number[]): number {
    return Math.round(arr.reduce((s, v) => s + v, 0) / arr.length);
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  exportToExcel() {
    const toExcelRow = (r: CentreStayRow, label?: string) => ({
      'Centre': label ?? r.centreName,
      'New Admissions (Count)': r.newCount,
      'New Admissions (Avg Days)': r.newAvgDays,
      'Follow-Up Admissions (Count)': r.followUpCount,
      'Follow-Up Admissions (Avg Days)': r.followUpAvgDays,
      'Carry-Forward (Count)': r.carryForwardCount,
      'Carry-Forward (Avg Days)': r.carryForwardAvgDays,
      'Total (Count)': r.totalCount,
      'Total (Avg Days)': r.totalAvgDays
    });

    const rows = this.tableRows.map(r => toExcelRow(r));
    rows.push(toExcelRow(this.grandTotalRow, 'Grand Total'));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Average Stay');
    XLSX.writeFile(wb, `average-stay-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }
}